"use client";

import { useQuery } from "@tanstack/react-query";
import { FolderIcon, Loader2, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useI18n } from "@/locales/client";
import { useTRPC } from "@/trpc/client";
import { useGetStudySetFolders } from "../api/use-get-study-set-folders";
import { useUpdateFolders } from "../api/use-update-folders";
import useMoveFolderModal from "../hooks/use-add-to-folder-modal";
import useNewFolderModal from "../hooks/use-new-folder-modal";

export const ManageFoldersForm = () => {
  const t = useI18n();
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(
    new Set(),
  );
  const [initialFolders, setInitialFolders] = useState<Set<string>>(new Set());
  const { onClose, studySetId } = useMoveFolderModal();
  const { onOpen: onOpenFolder } = useNewFolderModal();
  const trpc = useTRPC();
  const { data: folders, status } = useQuery(
    trpc.folders.getUserFolders.queryOptions(undefined, {
      staleTime: Number.POSITIVE_INFINITY,
    }),
  );
  const { data: studySetFolders, status: studySetFolderStatus } =
    useGetStudySetFolders(studySetId!);
  const updateFoldersMutation = useUpdateFolders();

  useEffect(() => {
    if (!studySetFolders) return;
    const folderIds = new Set(studySetFolders.map((folder) => folder.id));
    setSelectedFolders(folderIds);
    setInitialFolders(folderIds);
  }, [studySetFolders]);

  const handleSubmit = async () => {
    if (!studySetId) return;

    const foldersToAdd = [...selectedFolders].filter(
      (id) => !initialFolders.has(id),
    );
    const foldersToRemove = [...initialFolders].filter(
      (id) => !selectedFolders.has(id),
    );

    updateFoldersMutation.mutate(
      {
        id: studySetId,
        foldersToAdd,
        foldersToRemove,
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  const toggleFolder = (folderId: string) => {
    setSelectedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const hasChanges = () => {
    if (selectedFolders.size !== initialFolders.size) return true;
    return [...selectedFolders].some((id) => !initialFolders.has(id));
  };

  const isLoading = status === "pending" || studySetFolderStatus === "pending";
  const isError = status === "error" || studySetFolderStatus === "error";

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <Loader2 className="size-8 animate-spin mb-2" />
        <p>{t("loadingFolders")}</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-destructive">
        <p className="font-medium">{t("somethingWentWrong")}</p>
        <p className="text-sm text-muted-foreground mt-1">
          {t("pleaseTryAgainLater")}
        </p>
        <Button variant="outline" className="mt-4" onClick={onClose}>
          {t("close")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {!folders?.length ? (
          <div className="py-8 w-full border border-dashed rounded-2xl px-2">
            <div className="flex flex-col items-center justify-center w-full gap-3 h-full">
              <div className="size-12 grid place-items-center rounded border bg-muted/40">
                <FolderIcon className="size-6" />
              </div>
              <div className="flex flex-col justify-center items-center gap-1 text-center">
                <h3 className="font-semibold text-base leading-6">
                  {t("noFoldersCreatedYet")}
                </h3>
                <p className="text-sm font-normal leading-5 text-muted-foreground">
                  {t("noFoldersInLibrary")}
                </p>
              </div>

              <Button
                onClick={onOpenFolder}
                variant="ghost"
                size="sm"
                className="flex items-center gap-2 text-brand-primary"
              >
                <Plus className="size-4" />
                <span>{t("addFolder")}</span>
              </Button>
            </div>
          </div>
        ) : (
          <ScrollArea className="h-[280px] pr-4">
            <div className="grid gap-2">
              {folders.map((folder) => (
                <label
                  key={folder.id}
                  htmlFor={folder.id}
                  className="group flex items-center space-x-3 rounded-lg border p-3 hover:bg-accent transition-colors cursor-pointer w-full"
                >
                  <Checkbox
                    id={folder.id}
                    checked={selectedFolders.has(folder.id)}
                    onCheckedChange={() => toggleFolder(folder.id)}
                  />
                  <span className="text-sm font-medium">{folder.name}</span>
                </label>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>

      <div className="flex flex-col-reverse gap-y-4 md:gap-y-0 md:flex-row w-full md:items-center md:justify-between">
        <Button type="button" variant="ghost" onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!hasChanges() || updateFoldersMutation.isPending}
        >
          {updateFoldersMutation.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          {updateFoldersMutation.isPending ? t("saving") : t("saveChanges")}
        </Button>
      </div>
    </div>
  );
};
